// src/catalog/components/CatalogEntryCard.jsx

import { useTranslation } from "react-i18next";
import AVIcon from "../../components/icons/AllVoicedIcons";
import {
  ApplicationButton,
  ApplicationTooltip,
} from "../../components/application";

import {
  formatLabel,
  getCatalogCardFacetLabels,
  getEntryShortDescription,
  getEntryTitle
} from "../utils/catalogDisplay";
import { getCatalogEntryPrimaryAction } from "../utils/catalogAccess";

export default function CatalogEntryCard({
  entry,
  onLearnMore,
  onLaunch,
  onOpen,
  onToggleFavorite,
  onTogglePinned
}) {
  const { t } = useTranslation();

  if (!entry) return null;

  const title = getEntryTitle(entry, t);
  const description = getEntryShortDescription(entry, t);
  const facetLabels =
    getCatalogCardFacetLabels(entry, { maxFacets: 3 });
  const hasMedia = Boolean(entry.thumbnailUrl);

  const primaryLabel =
    formatLabel(entry.primaryType) || "Catalog entry";

  const lifecycleLabel = formatLabel(entry.lifecycleStatus);
  const deliveryModeLabel = formatLabel(entry.deliveryMode);

  const primaryAction = getCatalogEntryPrimaryAction(entry);
  const directLaunchAvailable = !primaryAction.disabled;
  const launchLabel = primaryAction.label;
  const learnMoreLabel = "Learn more";

  const canFavorite =
    typeof onToggleFavorite === "function";

  const isFavorite =
    entry.viewerContext?.isFavorite === true;

  const canPin =
    typeof onTogglePinned === "function";

  const isPinned =
    entry.viewerContext?.isPinned === true;

  const favoriteLabel = isFavorite
    ? "Remove from saved"
    : "Save for later";

  const pinLabel = isPinned
    ? "Remove from dashboard"
    : "Pin to dashboard";
  const hasPersonalActions = canFavorite || canPin;

  const handleLearnMore = () => {
    if (onLearnMore) {
      onLearnMore(entry);
      return;
    }

    onOpen?.(entry);
  };

  const handleLaunch = () => {
    if (!directLaunchAvailable) return;
    onLaunch?.(entry);
  };

  const handleToggleFavorite = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onToggleFavorite?.(entry);
  };

  const handleTogglePinned = (event) => {
    event.preventDefault();
    event.stopPropagation();

    onTogglePinned?.(entry);
  };

  return (
    <article
      className={[
        "catalog-entry-card",
        hasMedia ? "catalog-entry-card--has-media" : "catalog-entry-card--no-media",
        hasPersonalActions ? "catalog-entry-card--has-actions" : ""
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="catalog-entry-card__image">
        {hasMedia ? (
          <img
            src={entry.thumbnailUrl}
            alt=""
            loading="lazy"
          />
        ) : (
          <div className="catalog-entry-card__placeholder">
            {primaryLabel}
          </div>
        )}
      </div>

      {hasPersonalActions && (
        <div className="catalog-entry-card__image-actions">
          {canFavorite && (
            <ApplicationTooltip content={favoriteLabel}>
              <button
                type="button"
                className={[
                  "catalog-entry-card__icon-button",
                  "catalog-entry-card__favorite-button",
                  isFavorite ? "is-active" : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-label={favoriteLabel}
                aria-pressed={isFavorite}
                onClick={handleToggleFavorite}
              >
                <AVIcon
                  icon={isFavorite ? "heart2" : "heart1"}
                  size={18}
                />
              </button>
            </ApplicationTooltip>
          )}

          {canPin && (
            <ApplicationTooltip content={pinLabel}>
              <button
                type="button"
                className={[
                  "catalog-entry-card__icon-button",
                  "catalog-entry-card__pin-button",
                  isPinned ? "is-active" : ""
                ]
                  .filter(Boolean)
                  .join(" ")}
                aria-label={pinLabel}
                aria-pressed={isPinned}
                onClick={handleTogglePinned}
              >
                <AVIcon
                  icon={isPinned ? "pin2" : "pin1"}
                  size={18}
                />
              </button>
            </ApplicationTooltip>
          )}
        </div>
      )}

      <div className="catalog-entry-card__content">
        <div className="catalog-entry-card__meta">
          <span>{primaryLabel}</span>

          {lifecycleLabel && (
            <span>{lifecycleLabel}</span>
          )}

          {deliveryModeLabel && (
            <span>{deliveryModeLabel}</span>
          )}
        </div>

        <h3>{title}</h3>

        {description && (
          <p>{description}</p>
        )}

        {facetLabels.length > 0 && (
          <div className="catalog-entry-card__tags">
            {facetLabels.map((label) => (
              <span key={label}>
                {label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="catalog-entry-card__footer catalog-entry-card__footer--split">
        <ApplicationButton
          variant="secondary"
          className="catalog-entry-card__secondary-button"
          onClick={handleLearnMore}
        >
          {learnMoreLabel}
        </ApplicationButton>

        <ApplicationButton
          variant="primary"
          className="catalog-entry-card__primary-button"
          disabled={!directLaunchAvailable}
          onClick={handleLaunch}
        >
          {launchLabel}
        </ApplicationButton>
      </div>
    </article>
  );
}
